import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { ChatBotStore } from '../stores/chatBotStore';
import { ChatBotConfig, ChatMessage } from '../types';
import { Box, Paper, Typography, List, ListItem, Fade, IconButton, Avatar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import AndroidIcon from '@material-ui/icons/Android';
import { ChatBotHeader } from './chatBotHeader';
import { useStore } from '@roc/client-portal-shared/hooks';
import { MessageComponent } from './messageComponent';
import { SuggestionsBlock } from './suggestionsBlock';
import { ChatBotTextArea } from './textBoxComponents/chatBotTextArea';
import { useChatFollowLatestScroll } from '../hooks/useChatFollowLatestScroll';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    background: theme.palette.background.paper,
    borderRadius: '0',
    overflow: 'hidden',
  },
  messagesOuter: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  messagesContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: theme.spacing(3),
    background: theme.palette.background.paper,
    backdropFilter: 'blur(10px)',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(0, 0, 0, 0.05)',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(33, 57, 94, 0.5)',
      borderRadius: '4px',
      '&:hover': {
        background: 'rgba(33, 57, 94, 0.7)',
      },
    },
  },
  scrollToBottomButton: {
    position: 'absolute',
    bottom: theme.spacing(1.5),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
    width: 40,
    height: 40,
    borderRadius: '50%',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  messageItem: {
    display: 'flex',
    marginBottom: theme.spacing(2.5),
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    animation: '$slideIn 0.3s ease-out',
  },
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  userMessage: {
    justifyContent: 'flex-end',
  },
  botMessage: {
    justifyContent: 'flex-start',
  },
  avatar: {
    width: 36,
    height: 36,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  userAvatar: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  },
  botAvatar: {
    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
  },
  inputContainer: {
    display: 'flex',
    padding: theme.spacing(3),
    alignItems: 'center',
    background: '#F7F8FC',
    backdropFilter: 'blur(10px)',
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  typingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    background: theme.palette.background.paper,
    borderRadius: '18px',
    borderBottomLeftRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${theme.palette.divider}`,
  },
  typingDots: {
    display: 'flex',
    gap: '4px',
    '& span': {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: theme.palette.primary.main,
      animation: '$bounce 1.4s infinite ease-in-out',
      '&:nth-child(1)': {
        animationDelay: '-0.32s',
      },
      '&:nth-child(2)': {
        animationDelay: '-0.16s',
      },
    },
  },
  '@keyframes bounce': {
    '0%, 80%, 100%': {
      transform: 'scale(0.8)',
      opacity: 0.5,
    },
    '40%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 0.3,
      transform: 'scale(0.8)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.2)',
    },
  },
}));

interface ChatBotProps {
  store?: ChatBotStore;
  isFullScreen?: boolean;
  isEmbedded?: boolean;
}

export const ChatBot: React.FC<ChatBotProps> = observer(
  ({ store, isFullScreen, isEmbedded }) => {
    const classes = useStyles();
    const [inputValue, setInputValue] = useState('');
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const messagesContentRef = useRef<HTMLDivElement>(null);
    const { showJumpToLatest, onJumpToLatestClick, setFollowingLatest } =
      useChatFollowLatestScroll({
        scrollRootRef: messagesContainerRef,
        contentRef: messagesContentRef,
      });
    const isUserMessageSent = store.isUserMessageSent();

    useEffect(() => {
      return () => localStorage.removeItem('chatBotSessionId');
    }, []);

    // Initialize chat on mount
    useEffect(() => {
      if (store.messages.length === 0) {
        if (isEmbedded && isFullScreen && localStorage.getItem('chatBotSessionId')) {
          store.getMessagesHistory();
        } else {
          store.initialize();
        }
      }
    }, [store]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        store.destroy();
      };
    }, [store]);

    const handleSend = async () => {
      if (inputValue.trim() && !store.loading) {
        setFollowingLatest(true);
        await store.sendMessage(inputValue);
        setInputValue('');
      }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };


    return (
      <Paper elevation={0} className={classes.container}>
        <ChatBotHeader botName={store.config.botName} isFullScreen={isFullScreen} store={store} />
        <Box className={classes.messagesOuter}>
          <div className={classes.messagesContainer} ref={messagesContainerRef}>
            <div ref={messagesContentRef}>
              <List>
                {store.messages.map((message: ChatMessage, index) => (
                  <Fade in={true} key={message.id} timeout={300}>
                    <ListItem
                      className={`${classes.messageItem} ${message.sender === 'user'
                        ? classes.userMessage
                        : classes.botMessage}`}
                    >
                      <MessageComponent message={message} store={store} isFullScreen={isFullScreen} />
                    </ListItem>
                  </Fade>
                ))}
                {store.loading &&
                  store.messages.length > 0 &&
                  !store.messages[store.messages.length - 1]?.isStreaming && (
                    <Fade in={true}>
                      <ListItem
                        className={`${classes.messageItem} ${classes.botMessage}`}
                      >
                        <Avatar className={`${classes.avatar} ${classes.botAvatar}`}>
                          <AndroidIcon fontSize="small" />
                        </Avatar>
                        <Box className={classes.typingIndicator}>
                          <Box className={classes.typingDots}>
                            <span />
                            <span />
                            <span />
                          </Box>
                          <Typography variant="body2" style={{ color: '#64748b', fontSize: '13px' }}>
                            {store.config.botName} is typing...
                          </Typography>
                        </Box>
                      </ListItem>
                    </Fade>
                  )}
              </List>
            </div>
          </div>
          <Fade in={showJumpToLatest} unmountOnExit>
            <IconButton
              className={classes.scrollToBottomButton}
              onClick={onJumpToLatestClick}
              aria-label="Scroll to latest messages"
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Fade>
        </Box>
        <Box className={classes.inputContainer}>
          <ChatBotTextArea
            value={inputValue}
            setValue={setInputValue}
            onKeyPress={handleKeyPress}
            disabled={store.loading}
            handleSend={handleSend}
            store={store}
          />
          {isUserMessageSent && (
            <SuggestionsBlock store={store} />
          )}
        </Box>
      </Paper >
    );
  }
);
